import React from 'react';
import { formatTime } from '../../utils/date';
import { CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useGetMeTodayQuery } from '../../store/api/localApi';
import { StudentDayStatus, StudentDayStateShape } from '../../types/attendance-v2';

/**
 * Today strip for the parent's per-kid home page.
 *
 * Picks the rollup for the active student out of the /me/today response —
 * the endpoint returns one rollup per kid the parent is linked to, so we
 * filter client-side rather than making the endpoint per-student.
 *
 * Five visual states drive copy + colour:
 *   EXPECTED — pre-arrival or no signal yet. Calm, factual.
 *   HERE     — kid is on-site now. Green reassurance.
 *   GONE     — kid was picked up. White receipt with timestamps.
 *   NO_SHOW  — expected today but never arrived. Rose alert.
 *   PLANNED_ABSENT — admin/parent filed an absence (placeholder).
 */
const TodayStrip: React.FC<{ studentId: string }> = ({ studentId }) => {
  const { data, isLoading } = useGetMeTodayQuery(undefined, {
    // Light poll while the parent has the app open — drop-off and pickup
    // events tick over without a refresh. 60s feels right; the response
    // is small and the per-kid card stays calm between ticks.
    pollingInterval: 60_000,
  });

  if (isLoading) {
    return (
      <section
        data-testid="today-strip-loading"
        className="bg-white rounded-lg border border-slate-100 p-4"
      >
        <p className="text-xs uppercase tracking-wide text-slate-500">Today</p>
        <p className="mt-2 text-sm text-slate-400">Loading…</p>
      </section>
    );
  }

  const state = data?.today.find((row) => row.studentId === studentId);
  if (!state) {
    // Parent has a kid linked but the endpoint didn't return them. Likely
    // a fresh registration that hasn't propagated yet — surface nothing
    // rather than an alarming "EXPECTED" with no context.
    return null;
  }

  switch (state.status) {
    case StudentDayStatus.HERE:
      return <HereStrip state={state} />;
    case StudentDayStatus.GONE:
      return <GoneStrip state={state} />;
    case StudentDayStatus.SESSION_ENDED:
      return <SessionEndedStrip state={state} />;
    case StudentDayStatus.NO_SHOW:
      return <NoShowStrip state={state} />;
    case StudentDayStatus.PLANNED_ABSENT:
      return <PlannedAbsentStrip state={state} />;
    case StudentDayStatus.EXPECTED:
    default:
      return <ExpectedStrip />;
  }
};

const ExpectedStrip: React.FC = () => (
  <section
    data-testid="today-strip-expected"
    className="bg-white rounded-lg border border-slate-100 p-4"
  >
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today</h3>
      <span className="text-xs text-slate-400">Expected</span>
    </div>
    <p className="text-sm text-slate-700">Nothing logged yet — staff will check in on arrival.</p>
  </section>
);

const HereStrip: React.FC<{ state: StudentDayStateShape }> = ({ state }) => {
  const activeSpan = state.spans.find((s) => !s.outAt) ?? state.spans[state.spans.length - 1];
  return (
    <section
      data-testid="today-strip-here"
      className="bg-emerald-50 rounded-lg border border-emerald-200 p-4"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Today</h3>
        <span className="inline-flex items-center text-xs font-semibold text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" />
          Here now
        </span>
      </div>
      <div className="flex items-start gap-3">
        <CheckIcon className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-slate-900">
            Checked in at {formatTime(state.firstCheckInAt) || '—'}
            {state.firstCheckInBy ? ` by ${state.firstCheckInBy}` : ''}
          </p>
          {activeSpan && (
            <p className="text-xs text-emerald-700 mt-0.5">
              In {activeSpan.className}
              {activeSpan.sessionIds.length > 1 && (
                <span className="ml-1 italic text-slate-500">· continuous across sessions</span>
              )}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const GoneStrip: React.FC<{ state: StudentDayStateShape }> = ({ state }) => {
  return (
    <section
      data-testid="today-strip-gone"
      className="bg-white rounded-lg border border-slate-200 p-4"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today</h3>
        <span className="text-xs text-slate-500">Day complete</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-start gap-3">
          <CheckIcon className="w-4 h-4 text-slate-400 mt-0.5" />
          <p className="text-sm text-slate-700">
            Checked in <strong>{formatTime(state.firstCheckInAt) || '—'}</strong>
            {state.firstCheckInBy ? ` by ${state.firstCheckInBy}` : ''}
          </p>
        </div>
        <div className="flex items-start gap-3">
          <ArrowRightIcon className="w-4 h-4 text-slate-400 mt-0.5" />
          <p className="text-sm text-slate-700">
            Picked up <strong>{formatTime(state.lastCheckOutAt) || '—'}</strong>
            {state.lastCheckOutBy ? ` by ${state.lastCheckOutBy}` : ''}
          </p>
        </div>
        {state.spans.length > 0 && (
          <p className="text-xs text-slate-500 mt-1 pl-7">
            {state.spans.length === 1
              ? state.spans[0].className
              : `${state.spans.length} sessions · ${state.spans
                  .map((s) => s.className)
                  .join(', ')}`}
          </p>
        )}
      </div>
    </section>
  );
};

/**
 * The session ended with the child still checked in and nobody has recorded
 * who collected them. This used to fall through to the "Picked up" receipt —
 * on 2026-09-28 a 2pm session was ended early and 28 families read that their
 * child had been collected. Staff record the real collection afterwards, at
 * which point the strip becomes the normal receipt.
 */
const SessionEndedStrip: React.FC<{ state: StudentDayStateShape }> = ({ state }) => (
  <section
    data-testid="today-strip-session-ended"
    className="bg-amber-50 rounded-lg border border-amber-200 p-4"
  >
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700">Today</h3>
      <span className="text-xs font-semibold text-amber-700">Session ended</span>
    </div>
    <p className="text-sm font-medium text-slate-900">
      {state.studentName}'s session ended at {formatTime(state.lastCheckOutAt) || '—'}.
    </p>
    <p className="text-xs text-amber-800 mt-1">
      Collection not yet confirmed. Staff will record who collected {state.studentName}, and this
      will update.
    </p>
    <p className="text-xs text-slate-600 mt-2">
      Checked in {formatTime(state.firstCheckInAt) || '—'}
      {state.firstCheckInBy ? ` by ${state.firstCheckInBy}` : ''}
    </p>
  </section>
);

const NoShowStrip: React.FC<{ state: StudentDayStateShape }> = ({ state }) => (
  <section
    data-testid="today-strip-noshow"
    className="bg-rose-50 rounded-lg border border-rose-200 p-4"
  >
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-rose-700">Today</h3>
      <span className="text-xs font-semibold text-rose-700">⚠ Not arrived</span>
    </div>
    <p className="text-sm font-medium text-slate-900">
      {state.studentName} was expected today but hasn't been checked in.
    </p>
    <p className="text-xs text-rose-800 mt-1">
      If they aren't going today, mark a planned absence so staff stop expecting them.
    </p>
  </section>
);

const PlannedAbsentStrip: React.FC<{ state: StudentDayStateShape }> = ({ state }) => (
  <section
    data-testid="today-strip-planned-absent"
    className="bg-purple-50 rounded-lg border border-purple-200 p-4"
  >
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-purple-700">Today</h3>
      <span className="text-xs font-semibold text-purple-700">Planned absent</span>
    </div>
    <p className="text-sm text-slate-700">{state.studentName} is marked absent for today.</p>
  </section>
);

export default TodayStrip;
